<script>
  import { createEventDispatcher } from "svelte";
  import Modal from "@components/Modal.svelte";
  import { ComboBox } from "carbon-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import {
    TextInput,
    TextArea,
    InfoBlock,
    Button,
  } from "paxray-components-svelte";
  import { PLUS_ICON, TRASH_ICON } from "@utils/icons.js";

  export let open = false;
  // Leer zum Anlegen, sonst das Projekt, das bearbeitet wird.
  export let projectToEdit = "";
  export let typeOptions = [];
  // Alle bestehenden Projekte, damit doppelte Namen auffallen.
  export let existingProjects = [];

  const dispatch = createEventDispatcher();

  let projectTypeIndex = 0;
  let newProjectName = "";
  let newProjectDescription = "";
  let newProjectNameError = false;
  let invalidTextInput = "";
  let projectPersonsMaxId = 1;
  let newProjectPersons = [
    { id: 0, name: "", email: "" },
    { id: 1, name: "", email: "" },
    { id: 2, name: "", email: "" },
  ];
  let selectedIds = [];
  // Ein Department pro Projekt; Configure Framework filtert spaeter darauf vor.
  let departmentOptions = [];
  let createDepartmentModalOpen = false;
  let newDepartmentName = "";
  let newDepartmentError = "";
  let creatingDepartment = false;

  $: if (typeOptions.length > 0 && projectTypeIndex >= typeOptions.length) {
    projectTypeIndex = 0;
  }
  const projectTypeKey = (project) =>
    project?.isPotentialScout || project?.type === "Potential Scout"
      ? "potentialScout"
      : "taskAnalysis";
  $: isTechnicalCheck =
    !projectToEdit && typeOptions[projectTypeIndex]?.key === "technicalCheck";
  $: newProjectType = projectToEdit
    ? projectTypeKey(projectToEdit)
    : isTechnicalCheck
      ? "taskAnalysis"
      : typeOptions[projectTypeIndex]?.key || "taskAnalysis";

  // Formular bei jedem Oeffnen neu befuellen.
  let wasOpen = false;
  $: if (open !== wasOpen) {
    wasOpen = open;
    if (open) initForm();
  }

  function initForm() {
    newProjectNameError = false;
    if (!projectToEdit) {
      projectTypeIndex = 0;
      newProjectName = "";
      newProjectDescription = "";
      newProjectPersons = emptyProjectValues(newProjectType).persons;
      projectPersonsMaxId = newProjectPersons.length;
      selectedIds = [];
      return;
    }
    newProjectName = projectToEdit.name || "";
    newProjectDescription = projectToEdit.description || "";
    const contacts = projectToEdit.projectContacts;
    if (projectToEdit.isPotentialScout) {
      newProjectPersons = [
        ...(Array.isArray(contacts) ? contacts : []),
        { id: 0, name: "", email: "" },
      ];
    } else {
      newProjectPersons = Array.from(
        { length: 3 },
        (_, index) => contacts?.[index] || { id: index, name: "", email: "" },
      );
    }
    selectedIds = projectToEdit.departments?.map((dept) => dept.id) || [];
  }

  $: {
    if (newProjectName) newProjectNameError = false;
  }
  $: projectNameAlreadyExists =
    Boolean(newProjectName.trim()) &&
    existingProjects.some(
      (project) =>
        project.id !== projectToEdit?.id &&
        String(project.name || "")
          .trim()
          .toLowerCase() === newProjectName.trim().toLowerCase(),
    );
  $: requiredProjectContactsComplete =
    newProjectType === "potentialScout" ||
    (newProjectPersons.slice(0, 2).length === 2 &&
      newProjectPersons
        .slice(0, 2)
        .every((person) => person.name.trim() && person.email.trim()));
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isInvalidEmail = (email) =>
    Boolean(email?.trim()) && !EMAIL_PATTERN.test(email.trim());
  $: invalidProjectContactEmail = newProjectPersons.some((person) =>
    isInvalidEmail(person.email),
  );
  $: duplicateProjectContactEmail = (() => {
    const emails = cleanContactPersons(newProjectPersons)
      .map((person) => person.email.trim().toLowerCase())
      .filter(Boolean);
    return new Set(emails).size !== emails.length;
  })();

  $: if (
    open &&
    !projectToEdit &&
    newProjectType === "potentialScout" &&
    newProjectPersons.length !== 1
  ) {
    newProjectPersons = [{ id: 0, name: "", email: "" }];
  }
  $: if (
    open &&
    !projectToEdit &&
    newProjectType === "taskAnalysis" &&
    newProjectPersons.length !== 3
  ) {
    newProjectPersons = [
      { id: 0, name: "", email: "" },
      { id: 1, name: "", email: "" },
      { id: 2, name: "", email: "" },
    ];
  }
  // Prefill when entering the Technical Check tab and clear when leaving it,
  // so sample data never ends up in a real project. The assignments stay
  // inline so Svelte reruns the dependent checks (contacts, duplicates).
  let technicalCheckPrefilled = false;
  $: if (!open) technicalCheckPrefilled = false;
  $: if (open && isTechnicalCheck !== technicalCheckPrefilled) {
    technicalCheckPrefilled = isTechnicalCheck;
    const values = isTechnicalCheck
      ? technicalCheckValues()
      : emptyProjectValues(newProjectType);
    newProjectName = values.name;
    newProjectDescription = values.description;
    newProjectNameError = false;
    newProjectPersons = values.persons;
    selectedIds = [];
  }
  // Only preselect an existing "Test" department. A missing one is created
  // on submit, so cancelling the dialog leaves no trace.
  $: if (isTechnicalCheck && !selectedIds.length) {
    const existing = departmentOptions.find(
      (department) => department.name?.trim().toLocaleLowerCase() === "test",
    );
    if (existing) selectedIds = [existing.id];
  }

  function technicalCheckValues() {
    const timestamp = formatTestProjectTimestamp(new Date());
    const [date, time] = timestamp.split(" ");
    return {
      name: `Test Project • ${date} • ${time}`,
      description: `This is a Test project created at ${date} ${time}`,
      persons: [
        { id: 0, name: "Project Lead", email: "project.lead@paxray.com" },
        {
          id: 1,
          name: "Data Protection Officer",
          email: "data.protection.officer@paxray.com",
        },
        { id: 2, name: "IT Contact", email: "it.contact@paxray.com" },
      ],
    };
  }

  function emptyProjectValues(projectType) {
    return {
      name: "",
      description: "",
      persons:
        projectType === "potentialScout"
          ? [{ id: 0, name: "", email: "" }]
          : [
              { id: 0, name: "", email: "" },
              { id: 1, name: "", email: "" },
              { id: 2, name: "", email: "" },
            ],
    };
  }

  function formatSurveyDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  async function createPotentialScoutDraft() {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 7);
    const contact = cleanContactPersons(newProjectPersons)[0];

    const response = await fetchWithSession("/api/knextractor/surveys", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: newProjectName,
        tasks: [],
        users: [],
        questions: [],
        start_date: formatSurveyDate(startDate),
        end_date: formatSurveyDate(endDate),
        reminder_days: null,
        reminder2_days: null,
        enabled: true,
        custom_tasks: false,
        language: "en",
        email_signature: "",
        contact_person: contact?.name || null,
        contact_person_email: contact?.email || null,
        invitation_paused_at: new Date().toISOString(),
      }),
    });
    const result = await response.json();
    if (!response.ok || result.error || !result.data?.id) {
      throw new Error(
        result.error || "Potential Scout project could not be created",
      );
    }
    return result.data.id;
  }

  async function createProject() {
    newProjectNameError = false;
    if (!newProjectName) {
      newProjectNameError = true;
      invalidTextInput = "Please enter a project name";
    }
    if (
      newProjectNameError ||
      departmentMissing ||
      invalidProjectContactEmail
    ) {
      return;
    }
    if (newProjectType === "potentialScout") {
      try {
        const surveyId = await createPotentialScoutDraft();
        open = false;
        dispatch("created", { type: "potentialScout", id: surveyId });
      } catch (error) {}
      return;
    }
    if (isTechnicalCheck && !selectedIds.length) {
      try {
        selectedIds = [await ensureTestDepartment()];
      } catch {
        return;
      }
    }
    const body = JSON.stringify({
      name: newProjectName,
      description: newProjectDescription,
      projectContacts: cleanContactPersons(newProjectPersons),
      departments: selectedIds,
    });
    fetchWithSession("/api/damprojects/projects", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body,
    })
      .then(async (response) => {
        const responseText = await response.text();
        let res;
        try {
          res = responseText ? JSON.parse(responseText) : {};
        } catch {
          throw new Error("The server returned an invalid response.");
        }
        if (!response.ok) {
          throw new Error(res.message || res.error || response.statusText);
        }
        return res;
      })
      .then((res) => {
        open = false;
        if (res.ok) {
          dispatch("created", { type: "taskAnalysis", id: res.data.id });
        }
      })
      .catch((error) => {});
  }

  function formatTestProjectTimestamp(date) {
    const pad = (value) => String(value).padStart(2, "0");
    return `${pad(date.getDate())}:${pad(date.getMonth() + 1)}:${String(date.getFullYear()).slice(-2)} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  // Testprojekte landen immer im Department "Test", das bei Bedarf angelegt wird.
  async function ensureTestDepartment() {
    await getDepartmentOptions();
    const existing = departmentOptions.find(
      (department) => department.name?.trim().toLocaleLowerCase() === "test",
    );
    if (existing) return existing.id;

    const response = await fetchWithSession("/api/damprojects/departments", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: "Test" }),
    });
    const data = await response.json().catch(() => ({}));
    const created = data?.data || data;
    if (!response.ok || data?.ok === false || !created?.id) {
      throw new Error(
        data?.error || data?.message || "Could not create the department.",
      );
    }
    departmentOptions = [...departmentOptions, created];
    return created.id;
  }

  $: if (open) getDepartmentOptions();
  $: departmentComboItems = departmentOptions
    .map((department) => ({ id: department.id, text: department.name }))
    .sort((a, b) => a.text.localeCompare(b.text));
  $: departmentMissing =
    !projectToEdit &&
    !isTechnicalCheck &&
    newProjectType !== "potentialScout" &&
    !selectedIds.length;
  $: newDepartmentNameExists = departmentOptions.some(
    (department) =>
      department.name?.trim().toLocaleLowerCase() ===
      newDepartmentName.trim().toLocaleLowerCase(),
  );

  async function getDepartmentOptions() {
    try {
      const response = await fetchWithSession("/api/damprojects/departments");
      const data = await response.json();
      if (response.ok && Array.isArray(data)) departmentOptions = data;
    } catch {
      // Ohne Liste bleibt das Projekt ohne Department anlegbar.
    }
  }

  function filterDepartmentOption(item, value) {
    if (!value) return true;
    return item.text.toLowerCase().includes(value.toLowerCase());
  }

  function openCreateDepartmentModal() {
    newDepartmentName = "";
    newDepartmentError = "";
    createDepartmentModalOpen = true;
  }

  async function createDepartment() {
    const name = newDepartmentName.trim();
    if (!name || newDepartmentNameExists || creatingDepartment) return;
    creatingDepartment = true;
    newDepartmentError = "";
    try {
      const response = await fetchWithSession("/api/damprojects/departments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = await response.json().catch(() => ({}));
      const created = data?.data || data;
      if (!response.ok || data?.ok === false || !created?.id) {
        throw new Error(
          data?.error || data?.message || "Could not create the department.",
        );
      }
      departmentOptions = [...departmentOptions, created];
      selectedIds = [created.id];
      createDepartmentModalOpen = false;
    } catch (error) {
      newDepartmentError = error?.message || "Could not create the department.";
    } finally {
      creatingDepartment = false;
    }
  }

  function cleanContactPersons(projectPersons) {
    return projectPersons
      .filter((x) => x.name && x.email)
      .map((x) => {
        const { id, role, ...rest } = x;
        if (typeof id === "number") {
          return rest;
        } else {
          return x;
        }
      });
  }

  function updateProject() {
    newProjectNameError = false;
    if (!newProjectName) {
      newProjectNameError = true;
      invalidTextInput = "Please enter a project name";
    }
    if (newProjectNameError || invalidProjectContactEmail) {
      return;
    }
    const body = JSON.stringify({
      name: newProjectName,
      description: newProjectDescription,
      projectContacts: cleanContactPersons(newProjectPersons),
      departments: selectedIds,
    });
    const projectId = projectToEdit.id;
    fetchWithSession(`/api/damprojects/projects/${projectId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: body,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.ok) dispatch("updated", { id: projectId, data: data.data });
        close();
      });
  }

  function close() {
    open = false;
    selectedIds = [];
  }

  function addNewProjectPerson() {
    newProjectPersons.at(-1).id = projectPersonsMaxId;
    projectPersonsMaxId++;
    newProjectPersons.push({ id: 0, name: "", email: "" });
    newProjectPersons = newProjectPersons;
  }
</script>

<Modal
  actions
  autowidth
  preventCloseOnClickOutside
  bind:open
  modalHeading={projectToEdit
    ? `Edit ${projectToEdit.name}`
    : "Create New Project"}
  showTooltipSlot={!projectToEdit && typeOptions.length > 1}
  primaryButtonText={projectToEdit ? "Save" : "Create Project"}
  buttonSize="default"
  primaryButtonVariant="secondary"
  secondaryButtonText="Cancel"
  secondaryButtonAction={close}
  on:close={() => (open = false)}
  primaryButtonAction={projectToEdit ? updateProject : createProject}
  primaryButtonNotAllow={!newProjectName ||
    projectNameAlreadyExists ||
    departmentMissing ||
    duplicateProjectContactEmail ||
    invalidProjectContactEmail ||
    (!projectToEdit && !requiredProjectContactsComplete)}
>
  <div slot="tooltip" class="project-type-hint-content">
    <p>
      <strong>Task Analysis</strong><br />For task mining initiatives. Choose
      it to analyze tasks in depth.
    </p>
    <p>
      <strong>Potential Scout</strong><br />For surveys. Choose it to ask
      teams directly and get a quick overview of where potential could be.
    </p>
  </div>
  <svelte:fragment slot="left-buttons">
    {#if projectToEdit && !projectToEdit.isPotentialScout}
      <div class="delete-project-action">
        <Button
          text="Delete Project"
          kind="secondary"
          on:click={() => {
            open = false;
            dispatch("delete", projectToEdit);
          }}
        />
      </div>
    {/if}
  </svelte:fragment>
  <div class="project-type">
    {#if !projectToEdit && typeOptions.length > 1}
      <ContentSwitcher
        options={typeOptions.map((option) => option.value)}
        bind:selected={projectTypeIndex}
      />
    {/if}
    {#if !projectToEdit}
      <InfoBlock
        type="info"
        message={isTechnicalCheck
          ? "Creates a Task Analysis project with sample data to test the setup."
          : newProjectType === "potentialScout"
            ? "Sends a short survey to your teams to show where potential could be."
            : "Creates a task mining project for a deep dive into the tasks of one of your departments."}
        showClose={false}
      />
    {/if}
    <TextInput
      id="new-project-name"
      placeholder="Enter a project name"
      labelText="Project Name"
      required
      bind:value={newProjectName}
      invalid={newProjectNameError || projectNameAlreadyExists}
      invalidText={projectNameAlreadyExists
        ? "A project with this name already exists."
        : invalidTextInput
          ? "Please enter a project name"
          : ""}
    />
    {#if newProjectType !== "potentialScout" && projectToEdit}
      <!-- Department ist nach dem Anlegen fest, beim Bearbeiten nur Anzeige -->
      <TextInput
        id="edit-project-department"
        labelText="Department"
        value={projectToEdit.departments
          ?.map((department) => department.name)
          .join(", ") || "No department"}
        readonly
      />
    {:else if newProjectType !== "potentialScout"}
      <div class="project-department">
        <div class="project-department-select">
          <div class="project-name-label">
            <label class="label" for="new-project-department">
              Department <span class="required-marker">*</span>
            </label>
          </div>
          <ComboBox
            id="new-project-department"
            placeholder="Search or select a department"
            items={departmentComboItems}
            selectedId={selectedIds[0]}
            shouldFilterItem={filterDepartmentOption}
            on:select={(event) => (selectedIds = [event.detail.selectedId])}
            on:clear={() => (selectedIds = [])}
          />
        </div>
        <Button
          kind="secondary"
          text="Create New Department"
          on:click={openCreateDepartmentModal}
        />
      </div>
    {/if}
  </div>

  <div class="contact-persons">
    <div id="contacttable">
      {#each newProjectPersons as person, index}
        <div class="contact-row">
          <div class="contact-col">
            <TextInput
              labelText={newProjectType === "potentialScout"
                ? "Contact Person"
                : index === 0
                  ? "Project Lead / Controller"
                  : index === 1
                    ? "Data Protection Officer"
                    : index === 2
                      ? "IT Contact"
                      : "Contact Person"}
              placeholder={newProjectType === "potentialScout"
                ? "e.g. Jane Doe"
                : index === 0
                  ? "e.g. Jane Doe"
                  : index === 1
                    ? "e.g. Jane Doe"
                    : "e.g. Jane Doe"}
              bind:value={person.name}
              required={newProjectType !== "potentialScout" && index < 2}
            />
          </div>
          <div class="contact-col">
            <TextInput
              labelText={newProjectType !== "potentialScout" && index < 2
                ? "E-Mail"
                : "E-Mail"}
              placeholder="e.g. jane.doe@company.com"
              bind:value={person.email}
              required={newProjectType !== "potentialScout" && index < 2}
              invalid={isInvalidEmail(person.email)}
              invalidText="Please enter a valid email address."
            />
          </div>
          {#if projectToEdit && projectToEdit.isPotentialScout}
            <div class="contact-col contact-col--action">
              {#if person.id}
                <Button
                  icon={TRASH_ICON}
                  kind="secondary"
                  on:click={() => {
                    newProjectPersons = newProjectPersons.filter(
                      (x) => x.id != person.id,
                    );
                  }}
                />
              {:else}
                <Button
                  icon={PLUS_ICON}
                  kind="secondary"
                  on:click={addNewProjectPerson}
                />
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
    {#if duplicateProjectContactEmail}
      <p class="contact-error">
        Each role must be filled by a different person.
      </p>
    {/if}
  </div>

  {#if newProjectType !== "potentialScout"}
    <div class="description-section">
      <TextArea
        labelText="Description"
        placeholder="Describe the project (optional)"
        rows="5"
        bind:value={newProjectDescription}
      />
      <p class="required-legend">
        <span class="required-marker">*</span> Required
      </p>
    </div>
  {/if}
</Modal>

<Modal
  actions
  xs
  preventCloseOnClickOutside
  bind:open={createDepartmentModalOpen}
  modalHeading="Create New Department"
  primaryButtonText="Create"
  primaryButtonVariant="secondary"
  secondaryButtonText="Cancel"
  primaryButtonAction={createDepartment}
  primaryButtonNotAllow={!newDepartmentName.trim() ||
    newDepartmentNameExists ||
    creatingDepartment}
>
  <TextInput
    id="new-department-name"
    labelText="Department Name"
    placeholder="Enter a department name"
    bind:value={newDepartmentName}
    invalid={newDepartmentNameExists || Boolean(newDepartmentError)}
    invalidText={newDepartmentNameExists
      ? "A department with this name already exists."
      : newDepartmentError}
  />
</Modal>

<style>
  #contacttable {
    width: 100%;
    padding-top: var(--control-gap);
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .project-type {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  :global(.project-type-hint-content) {
    width: min(20rem, calc(100vw - 2rem));
    white-space: normal;
    text-align: left;
    font-weight: 400;
  }

  :global(.project-type-hint-content p) {
    margin: 0 0 1em;
  }

  :global(.project-type-hint-content p:last-child) {
    margin-bottom: 0;
  }

  .project-department {
    display: flex;
    align-items: flex-end;
    gap: var(--control-gap);
  }

  .project-department-select {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  /* ComboBox an TextInput angleichen: eigenes Label oben, gleiche Rahmenfarbe */
  .project-department-select :global(.bx--list-box) {
    height: auto;
    max-height: none;
    border: solid var(--bw) var(--pax-black-inactive);
    border-radius: var(--br);
    background-color: var(--pax-white);
  }

  .project-department-select :global(.bx--list-box:hover) {
    background-color: var(--pax-white);
  }

  .project-department-select :global(.bx--list-box:focus-within) {
    outline: solid var(--bw) var(--pax-black);
  }

  /* Carbon-Padding rechts bleibt für Clear-/Menü-Icons erhalten */
  .project-department-select :global(.bx--text-input) {
    height: auto;
    padding-top: var(--s);
    padding-bottom: var(--s);
    padding-left: var(--s);
    border: none;
    border-radius: var(--br);
    background-color: transparent;
    font-size: 0.875rem;
    line-height: normal;
    color: var(--pax-black);
  }

  .project-department-select :global(.bx--text-input:focus) {
    outline: none;
  }

  .project-department-select :global(.bx--text-input::placeholder) {
    color: var(--pax-black-inactive);
  }

  .project-department-select :global(.bx--list-box__menu) {
    border-radius: var(--br);
  }

  .project-name-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .project-name-label .label {
    margin-bottom: var(--xs);
    color: var(--pax-black);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    line-height: 1rem;
    text-align: left;
  }

  .required-marker {
    color: var(--pax-black);
  }

  .required-legend {
    margin: 0.5rem 0 0;
    color: var(--pax-black);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
  }

  .delete-project-action :global(button) {
    background: var(--pax-danger, #ff0000) !important;
    border-color: var(--pax-danger, #ff0000) !important;
    color: var(--pax-white, #fff) !important;
  }

  .delete-project-action :global(button:hover) {
    background: #d90000 !important;
    border-color: #d90000 !important;
  }

  .contact-persons {
    margin: 0 0 var(--control-gap);
  }

  .contact-error {
    margin: var(--control-gap) 0 0;
    color: var(--pax-danger);
    font-size: 0.75rem;
    font-weight: 600;
  }

  .contact-row {
    display: flex;
    align-items: flex-start;
    gap: var(--control-gap);
    width: 100%;
  }

  .contact-row :global(.text-input) {
    margin-top: 0.5rem;
  }

  .contact-col {
    flex: 1;
  }

  .contact-col--action {
    flex: 0 0 auto;
    align-self: flex-end;
  }
</style>
